import {
  type SkeletonRegistry,
  skeletonRegistryListSchema,
} from '../schema';

export const SKELETON_REGISTRIES = skeletonRegistryListSchema.parse([
  {
    id: 'skeleton_btob_inquiry_default',
    label: 'BtoB Inquiry Default',
    description: '法人向け問い合わせの標準構成。',
    version: 1,
    status: 'active',
    priority: 100,
    tags: ['b2b', 'default'],
    familyId: 'btob_inquiry',
    requiredSections: ['hero', 'problem', 'solution', 'services', 'proof', 'cta', 'form'],
    optionalSections: ['features', 'comparison', 'review', 'faq', 'use_cases'],
    sectionOrder: ['hero', 'problem', 'solution', 'services', 'features', 'comparison', 'use_cases', 'proof', 'review', 'faq', 'cta', 'form'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 2,
      richMaxOptionalSections: 4,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: false,
      allowStickyMobileCta: false,
      recommendedPlacements: ['hero', 'footer'],
    },
    formPolicy: {
      required: true,
      canMergeIntoCta: false,
    },
    emotionalArc: 'hero（期待・興味）→ problem（共感・危機感）→ solution（安心・希望）→ services（具体的な理解）→ features（納得・理解）→ proof（信頼・確信）→ faq（不安解消）→ cta（行動決意）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: 'まずは相談する' },
      { position: 'proof直後', afterSectionType: 'proof', intensity: 'normal', suggestedLabel: 'お問い合わせ' },
      { position: '最下部', intensity: 'strong', suggestedLabel: '今すぐ相談する' },
    ],
  },
  {
    id: 'skeleton_saas_ai_inquiry_default',
    label: 'SaaS Inquiry Default',
    description: 'SaaS / AI 問い合わせの標準構成。',
    version: 1,
    status: 'active',
    priority: 100,
    tags: ['saas', 'default'],
    familyId: 'saas_ai_inquiry',
    requiredSections: ['hero', 'problem', 'solution', 'feature_list', 'proof', 'cta', 'form'],
    optionalSections: ['features', 'comparison', 'use_cases', 'faq'],
    sectionOrder: ['hero', 'problem', 'solution', 'feature_list', 'features', 'comparison', 'use_cases', 'proof', 'faq', 'cta', 'form'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 2,
      richMaxOptionalSections: 4,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: false,
      allowStickyMobileCta: false,
      recommendedPlacements: ['hero', 'footer'],
    },
    formPolicy: {
      required: true,
      canMergeIntoCta: false,
    },
    emotionalArc: 'hero（興味・可能性）→ problem（課題の自分ごと化）→ solution（解決の道筋）→ feature_list（具体的な理解）→ use_cases（活用イメージ）→ proof（導入の安心感）→ faq（懸念の解消）→ cta（試してみたい）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: '無料で試す' },
      { position: 'feature_list直後', afterSectionType: 'feature_list', intensity: 'normal', suggestedLabel: 'デモを見る' },
      { position: '最下部', intensity: 'strong', suggestedLabel: '今すぐ始める' },
    ],
  },
  {
    id: 'skeleton_regional_service_inquiry_default',
    label: 'Regional Service Default',
    description: '地域サービス問い合わせの標準構成。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['regional', 'default'],
    familyId: 'regional_service_inquiry',
    requiredSections: ['hero', 'services', 'reasons', 'proof', 'cta', 'form'],
    optionalSections: ['review', 'flow', 'faq', 'access', 'message'],
    sectionOrder: ['hero', 'services', 'reasons', 'proof', 'review', 'flow', 'faq', 'message', 'access', 'cta', 'form'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 3,
      richMaxOptionalSections: 4,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: true,
      allowStickyMobileCta: false,
      recommendedPlacements: ['hero', 'footer'],
    },
    formPolicy: {
      required: true,
      canMergeIntoCta: true,
    },
    emotionalArc: 'hero（安心・信頼）→ services（具体的な対応内容）→ reasons（選ばれる理由）→ proof（実績で信頼）→ review（実際の声で安心）→ flow（進め方が分かる）→ faq（不安解消）→ cta（相談してみよう）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: 'まずは無料相談' },
      { position: 'review直後', afterSectionType: 'review', intensity: 'normal', suggestedLabel: 'お問い合わせ' },
      { position: '最下部', intensity: 'strong', suggestedLabel: '今すぐ相談する' },
    ],
  },
  {
    id: 'skeleton_store_reservation_default',
    label: 'Store Reservation Default',
    description: '店舗予約導線の標準構成。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['store', 'default'],
    familyId: 'store_reservation',
    requiredSections: ['hero', 'services', 'review', 'access', 'cta'],
    optionalSections: ['pricing', 'faq', 'offer', 'proof'],
    sectionOrder: ['hero', 'services', 'review', 'pricing', 'proof', 'faq', 'offer', 'access', 'cta'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 2,
      richMaxOptionalSections: 4,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: true,
      allowStickyMobileCta: true,
      recommendedPlacements: ['hero', 'middle', 'mobile_sticky', 'footer'],
    },
    formPolicy: {
      required: false,
      canMergeIntoCta: true,
    },
    emotionalArc: 'hero（魅力・行ってみたい）→ services（具体的なメニュー）→ review（実際の満足度）→ pricing（納得感）→ access（行きやすさ）→ cta（予約しよう）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: '今すぐ予約する' },
      { position: 'pricing直後', afterSectionType: 'pricing', intensity: 'normal', suggestedLabel: '予約する' },
      { position: '最下部', intensity: 'strong', suggestedLabel: '今すぐ予約する' },
    ],
  },
  {
    id: 'skeleton_d2c_purchase_default',
    label: 'D2C Purchase Default',
    description: 'D2C 購入導線の標準構成。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['d2c', 'default'],
    familyId: 'd2c_purchase',
    requiredSections: ['hero', 'benefit', 'features', 'pricing', 'cta'],
    optionalSections: ['review', 'offer', 'faq', 'proof'],
    sectionOrder: ['hero', 'benefit', 'features', 'review', 'proof', 'pricing', 'offer', 'faq', 'cta'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 3,
      richMaxOptionalSections: 4,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: true,
      allowStickyMobileCta: true,
      recommendedPlacements: ['hero', 'middle', 'mobile_sticky', 'footer'],
    },
    formPolicy: {
      required: false,
      canMergeIntoCta: true,
    },
    emotionalArc: 'hero（欲しい・気になる）→ benefit（手に入る未来）→ features（品質の根拠）→ review（使った人の声）→ proof（実績で安心）→ pricing（納得感）→ offer（今がチャンス）→ cta（買おう）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: '今すぐ購入する' },
      { position: 'review直後', afterSectionType: 'review', intensity: 'normal', suggestedLabel: '購入する' },
      { position: 'offer直後', afterSectionType: 'offer', intensity: 'strong', suggestedLabel: '限定価格で手に入れる' },
    ],
  },
  {
    id: 'skeleton_seminar_application_default',
    label: 'Seminar Application Default',
    description: 'セミナー申込の標準構成。',
    version: 1,
    status: 'active',
    priority: 92,
    tags: ['seminar', 'default'],
    familyId: 'seminar_application',
    requiredSections: ['hero', 'overview', 'speaker', 'cta', 'form'],
    optionalSections: ['offer', 'proof', 'faq', 'target_audience'],
    sectionOrder: ['hero', 'overview', 'speaker', 'target_audience', 'proof', 'offer', 'faq', 'cta', 'form'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 2,
      richMaxOptionalSections: 4,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: false,
      allowStickyMobileCta: true,
      recommendedPlacements: ['hero', 'footer'],
    },
    formPolicy: {
      required: true,
      canMergeIntoCta: false,
    },
    emotionalArc: 'hero（興味・これ参加したい）→ overview（概要把握）→ speaker（信頼できる登壇者）→ target_audience（自分向けだ）→ proof（過去の実績）→ faq（迷いの解消）→ cta（申し込もう）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: '申し込む' },
      { position: 'speaker直後', afterSectionType: 'speaker', intensity: 'normal', suggestedLabel: '参加を申し込む' },
      { position: '最下部', intensity: 'strong', suggestedLabel: '今すぐ申し込む' },
    ],
  },
  {
    id: 'skeleton_education_application_default',
    label: 'Education Application Default',
    description: '教育講座申込の標準構成。',
    version: 1,
    status: 'active',
    priority: 92,
    tags: ['education', 'default'],
    familyId: 'education_application',
    requiredSections: ['hero', 'course_or_curriculum', 'instructor', 'proof', 'cta', 'form'],
    optionalSections: ['faq', 'pricing', 'target_audience', 'review'],
    sectionOrder: ['hero', 'target_audience', 'course_or_curriculum', 'instructor', 'proof', 'pricing', 'review', 'faq', 'cta', 'form'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 3,
      richMaxOptionalSections: 4,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: false,
      allowStickyMobileCta: false,
      recommendedPlacements: ['hero', 'footer'],
    },
    formPolicy: {
      required: true,
      canMergeIntoCta: false,
    },
    emotionalArc: 'hero（変わりたい・学びたい）→ target_audience（自分のための講座だ）→ course_or_curriculum（具体的な中身）→ instructor（信頼できる講師）→ proof（実績）→ review（先輩の声）→ pricing（投資として妥当）→ faq（残る不安の解消）→ cta（申し込もう）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: '無料体験に申し込む' },
      { position: 'review直後', afterSectionType: 'review', intensity: 'normal', suggestedLabel: '受講を申し込む' },
      { position: '最下部', intensity: 'strong', suggestedLabel: '今すぐ申し込む' },
    ],
  },
  {
    id: 'skeleton_recruitment_entry_default',
    label: 'Recruitment Entry Default',
    description: '採用応募導線の標準構成。',
    version: 1,
    status: 'active',
    priority: 94,
    tags: ['recruitment', 'default'],
    familyId: 'recruitment_entry',
    requiredSections: ['hero', 'work_style', 'people', 'proof', 'cta', 'form'],
    optionalSections: ['message', 'faq', 'review'],
    sectionOrder: ['hero', 'message', 'work_style', 'people', 'proof', 'review', 'faq', 'cta', 'form'],
    densityPolicy: {
      lightMaxOptionalSections: 1,
      standardMaxOptionalSections: 2,
      richMaxOptionalSections: 3,
    },
    duplicateRules: [{ sectionType: 'cta', maxCount: 1 }],
    ctaPolicy: {
      allowInlineSubCta: false,
      allowStickyMobileCta: false,
      recommendedPlacements: ['hero', 'footer'],
    },
    formPolicy: {
      required: true,
      canMergeIntoCta: false,
    },
    emotionalArc: 'hero（ここで働きたい）→ message（会社の想い・ビジョン）→ work_style（働き方・環境）→ people（一緒に働く人）→ proof（実績・成長性）→ faq（条件面の確認）→ cta（応募しよう）',
    ctaPlacements: [
      { position: 'hero直下', afterSectionType: 'hero', intensity: 'soft', suggestedLabel: '募集要項を見る' },
      { position: 'people直後', afterSectionType: 'people', intensity: 'normal', suggestedLabel: 'エントリーする' },
      { position: '最下部', intensity: 'strong', suggestedLabel: '今すぐ応募する' },
    ],
  },
]);

export const skeletonRegistryMap = new Map(
  SKELETON_REGISTRIES.map((skeleton) => [skeleton.id, skeleton] as const),
);

export function getSkeletonRegistry(skeletonId: string): SkeletonRegistry | undefined {
  return skeletonRegistryMap.get(skeletonId);
}

export function listSkeletonsForFamily(familyId: string): SkeletonRegistry[] {
  return SKELETON_REGISTRIES.filter((skeleton) => skeleton.familyId === familyId);
}
